import React, { useState, useMemo } from "react";
import moment from "moment-timezone";
import { useGetUserCalendarEventsQuery } from "@/features/calendarEvents/calendarEventsApiSlice";
import { useGetUserMeetingPrepsQuery } from "@/features/userMeetingPreps/userMeetingPrepsApiSlice";
// 22 May 2025
import { useGetContactsQuery } from "@/pages/admin/contacts/contactsApiSlice"

import { CalendarDaysIcon } from "@heroicons/react/24/outline"; // optional icon

// 20 May 2025
import EtMeetingCard from "@/components/meetings/EtMeetingCard";
import { CircularProgressbarWithChildren } from 'react-circular-progressbar'


const EtTrinityReviewPrep = ({ userId, onClose, userEmail="" }) => {
    
  // First Created - 19 May 2025
  // Author - Atul Pandey
  // Purpose - Allow user to revisit prep for the next meeting
  // Updated - 23 May 2025 - inherited code from EtTrinityPreviewMeetings
  
  // 0 - Validate props data
  // console.log('userEmail in EtTrinityReviewPrep props', userEmail)

  // 1 - State variables
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);

  // 2 - Fetch User Meetings Data from UserCalendarEvents DB
  const { data:eventsData, isLoading, isError } = useGetUserCalendarEventsQuery(); 
  const { data: meetingPrepData = [] } = useGetUserMeetingPrepsQuery();
  const { data: contactsData } = useGetContactsQuery();// 22 May 2025
  
  const extractEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id]);
  };

  const meetingPreps = extractEntitiesFromAdapter(meetingPrepData)
  const relevantContacts = extractEntitiesFromAdapter(contactsData)

  // console.log('eventsData', eventsData)
  // console.log('meetingPreps', meetingPreps)
  //  console.log('relevant Contacts:', relevantContacts)

  // 3a - Get local timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // 3b - Define start and end of today in user's timezone
  const startOfToday = moment().tz(timezone).startOf("day");
  const endOfToday = moment().tz(timezone).endOf("day");

  // 3c Filter events for today
  const meetingsToday = useMemo(() => {
  if (!eventsData || !meetingPreps) return [];

  return eventsData
    .filter(event => {
      if (event.isAllDay) return false;

      const eventStart = moment(event.startTime).tz(timezone);
      return eventStart.isBetween(startOfToday, endOfToday, null, "[]");
    })
    .map(event => {
      // Check for prep
      const matchingPrep = meetingPreps.find(
        prep => prep.calendarMeetingId?._id === event?._id
      );

      const hasPrep = matchingPrep?.meetingPrepNotes?.length > 0;

      return {
        ...event,
        hasPrep,
        matchingPrep, // ✅ 20 May 2025 NEW: pass this along to MeetingCard
      };
    })
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [eventsData, meetingPrepData, timezone]);

  // 4 - Compute Meeting Prep Progress
  const totalMeetings = meetingsToday.length;
  const preppedMeetings = meetingsToday.filter(m => m.hasPrep).length;
  const prepProgress = totalMeetings > 0 ? Math.round((preppedMeetings / totalMeetings) * 100) : 0;

  return (
    <div className="p-4 m-2 flex flex-col items-center">

      {isLoading && <p className="text-sm text-gray-500">Loading meetings...</p>}
      {isError && <p className="text-sm text-red-500">Error loading meetings</p>}

      <div className="flex justify-center items-start w-full h-full overflow-y-auto px-4 mb-2">
        <div className="grid grid-cols-2 gap-6 w-full max-w-6xl">
          <div className="grid grid-cols-10 justify-start items-center">
            <div className="col-span-9 text-lg font-bold text-gray-800 mb-2 flex justify-center items-center gap-2">
              <CalendarDaysIcon className="h-5 w-5 text-orange-500" />
              <p>Meetings for Today</p>
            </div>
            <div className="col-span-1 flex justify-end mx-2 px-2">
                <button
                  onClick={onClose}
                  className="text-sm text-blue-600 underline"
                >
                  Close
                </button>
              </div>
          </div>
          <div className="text-lg font-bold text-gray-800 mb-2 flex justify-center items-center gap-2">
            {/* <CalendarDaysIcon className="h-5 w-5 text-orange-500" /> */}
            <p>Your Prep</p>
          </div>
        </div>
      </div>
      
      {/* 20 May 2025 - Using Meeting Card component */}
      
      <div className="flex justify-center items-start w-full h-full overflow-y-auto px-4">
        <div className="grid grid-cols-2 gap-6 w-full h-full max-w-6xl">
            {/* Left 5/6: Meeting Cards */}
            <div className="h-full overflow-y-auto p-2 rounded bg-white shadow-inner">
              {meetingsToday?.length >0 && meetingsToday?.map((meeting, index) => (
                <EtMeetingCard
                  key={meeting._id}
                  event={meeting}
                  index={index}
                  activeTab="preview"
                  matchingPrep={meeting.matchingPrep}
                  isActive={selectedMeetingId === meeting._id}
                  onClick={() => setSelectedMeetingId(meeting._id)}
                  userEmail={userEmail} // 21 May 2025
                  relevantContacts={relevantContacts} // 22 May 2025
                />
              ))}
              {meetingsToday?.length === 0 && !isLoading && (
                <div className="flex justify-center items-center h-full">
                  <p className="mx-1 px-1 text-lg text-gray-600">You have no meetings scheduled for today yet.</p>
                </div>
              )}
            </div>

            {/* Right 1/6: Circular Progress */}
            <div className="flex justify-center items-start h-full p-4">
              <div className="h-64 w-64 relative z-0">
                <CircularProgressbarWithChildren
                  value={prepProgress}
                  circleRatio={0.75}
                  styles={{
                    path: { stroke: '#552a9a', transform: 'rotate(-135deg)', transformOrigin: 'center center' },
                    trail: { stroke: '#d6d6d6', transform: 'rotate(-135deg)', transformOrigin: 'center center' }
                  }}
                >
                  <div><p className="text-6xl font-bold text-orange-600">{prepProgress}%</p></div>
                </CircularProgressbarWithChildren>
              </div>
            </div>
          </div>
      </div>
      

      {/* <div className="mt-4">
        <button
          onClick={onClose}
          className="text-sm text-blue-600 underline"
        >
          Close
        </button>
      </div> */}
    </div>
  );
};

export default EtTrinityReviewPrep;